import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api.js";
import { useWorkspace } from "./workspace.js";

/**
 * The workspace's name and logo.
 *
 * Kept apart from the auth context because the login screen needs it *before*
 * anyone is signed in — `/auth/branding` is the one settings read that does
 * not require a session.
 *
 * The defaults here match the server's. That matters: on a cold load the app
 * renders before the fetch resolves, and falling back to an empty string would
 * flash a nameless shell on every visit.
 */

export type Branding = {
  workspaceName: string;
  workspaceLogo: string | null;
};

const DEFAULT_BRANDING: Branding = {
  workspaceName: "Astrum Connect",
  workspaceLogo: null,
};

type BrandingContextValue = Branding & {
  /** Re-reads after an administrator saves, so the rail updates without a reload. */
  refresh: () => Promise<void>;
};

const BrandingContext = createContext<BrandingContextValue>({
  ...DEFAULT_BRANDING,
  refresh: async () => {},
});

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  // The active office's name is the "settings.name when present" the rollout
  // asks the shell to reflect. Read from the memberships list rather than a
  // per-office settings fetch — that name is already in hand, and global
  // branding remains the fallback. Requires WorkspaceProvider above this one.
  const { active } = useWorkspace();

  const refresh = useCallback(async () => {
    try {
      const next = await api.get<Branding>("/auth/branding");
      setBranding({
        workspaceName: next.workspaceName || DEFAULT_BRANDING.workspaceName,
        workspaceLogo: next.workspaceLogo ?? null,
      });
    } catch {
      // Branding is decoration. If this fails the app still has to work, so
      // the defaults stand rather than blocking render on a retry.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The active office's name wins over the global default when present, so a
  // person in the China office sees it named rather than "Astrum Connect".
  const effectiveName = active?.name?.trim() ? active.name : branding.workspaceName;

  // The tab title is part of the branding: several of these open at once in
  // practice, and "Astrum Connect" on all of them is no help.
  useEffect(() => {
    document.title = effectiveName;
  }, [effectiveName]);

  const value = useMemo(
    () => ({ ...branding, workspaceName: effectiveName, refresh }),
    [branding, effectiveName, refresh],
  );
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

/**
 * The one- or two-letter mark shown when there is no logo.
 *
 * Initials of the first two words, so "Astrum Connect" gives "AC" and a
 * single-word name gives one letter. Purely derived — there is no separate
 * setting for it, because a monogram that disagrees with the name is worse
 * than no monogram.
 */
export function brandInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "A";
  if (words.length === 1) return words[0]!.slice(0, 1).toUpperCase();
  return (words[0]!.slice(0, 1) + words[1]!.slice(0, 1)).toUpperCase();
}
